/**
 * Fumee cart upsell.
 *
 * Lives in its own asset rather than inline in the snippet. The drawer markup is
 * re-rendered (morphed) on every cart change, and a <script> tag introduced that
 * way never executes, so an inline handler here would be bound on a full page
 * load and never again. Everything below is delegated from the document and
 * bound exactly once, which survives any number of re-renders.
 */

(() => {
  const ADD_LABEL = '+ Add';

  /**
   * @param {Element} button
   * @param {string} text
   */
  const setLabel = (button, text) => {
    const label = button.querySelector('[data-upsell-button-label]') ?? button;
    label.textContent = text;
  };

  /**
   * @param {HTMLButtonElement} button
   */
  const addToCart = async (button) => {
    const variantId = Number(button.dataset.upsellVariantId);
    if (!variantId) return;

    button.disabled = true;
    setLabel(button, 'Adding');

    try {
      // The drawer is already open, so this adds without opening it again. The
      // sections come back with the add itself rather than in a second request.
      if (window.FumeeCart?.add) {
        await window.FumeeCart.add([{ id: variantId, quantity: 1 }], { openDrawer: false });
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

        document.dispatchEvent(new CustomEvent('fumee:cart:refresh'));
      }

      setLabel(button, 'Added');
    } catch (error) {
      console.error('Fumee cart upsell:', error);
      setLabel(button, ADD_LABEL);
    } finally {
      // The button is usually gone by now, replaced by the re-render. This only
      // matters when the refresh failed and the visitor needs another go.
      if (button.isConnected) button.disabled = false;
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const button = target.closest('[data-upsell-add-btn]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    addToCart(button);
  });
})();
